import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Plus, Upload, Trash2, Save, Download, Eye, Users } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { courriersApi, enfantsApi } from '../../utils/api';

const CATS = [
  { value: 'courrier', label: 'Courrier' },
  { value: 'contrat', label: 'Contrat' },
  { value: 'attestation', label: 'Attestation' },
  { value: 'autre', label: 'Autre' },
];

export default function CourriersPage() {
  const { getApiParams } = useApp();
  const [modeles, setModeles] = useState([]);
  const [champs, setChamps] = useState([]);
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState({ nom: '', categorie: 'courrier', contenu: '' });
  const [dirty, setDirty] = useState(false);
  const [enfants, setEnfants] = useState([]);
  const [enfantId, setEnfantId] = useState('');
  const [apercu, setApercu] = useState('');
  const [msg, setMsg] = useState('');
  const textRef = useRef(null);
  const fileRef = useRef(null);

  const loadList = useCallback(() => {
    courriersApi.getAll().then(({ data }) => { setModeles(data.modeles || []); setChamps(data.champs || []); }).catch(() => {});
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    enfantsApi.getAll({ ...getApiParams(), statut: 'actif' }).then(({ data }) => setEnfants(data || [])).catch(() => {});
  }, [getApiParams]);

  const openModele = (id) => {
    courriersApi.getById(id).then(({ data }) => {
      setSelId(id); setForm({ nom: data.nom, categorie: data.categorie || 'courrier', contenu: data.contenu || '' });
      setDirty(false); setApercu('');
    });
  };
  const nouveau = () => { setSelId(null); setForm({ nom: '', categorie: 'courrier', contenu: '' }); setDirty(true); setApercu(''); };

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 2500); };

  const save = async () => {
    if (!form.nom.trim()) { flash('Donnez un nom au modèle.'); return; }
    if (selId) { await courriersApi.update(selId, form); flash('Modèle enregistré.'); }
    else { const { data } = await courriersApi.create(form); setSelId(data.id); flash('Modèle créé.'); }
    setDirty(false); loadList();
  };
  const supprimer = async () => {
    if (!selId || !window.confirm('Supprimer ce modèle ?')) return;
    await courriersApi.delete(selId); nouveau(); loadList();
  };

  const insertChamp = (cle) => {
    const ta = textRef.current;
    const start = ta ? ta.selectionStart : form.contenu.length;
    const end = ta ? ta.selectionEnd : form.contenu.length;
    const next = form.contenu.slice(0, start) + cle + form.contenu.slice(end);
    setForm(f => ({ ...f, contenu: next })); setDirty(true);
    setTimeout(() => { if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = start + cle.length; } }, 0);
  };

  const onImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await courriersApi.importFile(fd);
      setSelId(null); setForm({ nom: data.nom || 'Modèle importé', categorie: 'courrier', contenu: data.contenu || '' });
      setDirty(true); flash('Document importé — vérifiez puis enregistrez.');
    } catch { flash('Import impossible.'); }
    e.target.value = '';
  };

  const voirApercu = () => {
    if (!selId) { flash('Enregistrez d’abord le modèle.'); return; }
    courriersApi.apercu(selId, enfantId).then(({ data }) => setApercu(data.contenu || '')).catch(() => {});
  };

  return (
    <div className="max-w-6xl">
      <div className="mb-4">
        <h1 className="font-serif text-[28px] text-navy-900 flex items-center gap-2"><FileText size={24} className="text-gold-600" /> Modèles &amp; courriers</h1>
        <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2" />
        <p className="text-navy-400 text-[13.5px]">Créez ou importez vos courriers et contrats types, modifiables ici. En choisissant un enfant, les champs (adresse, parents, mensualité…) se remplissent automatiquement.</p>
      </div>

      {msg && <div className="mb-3 text-[13px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg px-3 py-2 inline-block">{msg}</div>}

      <div className="grid md:grid-cols-[260px_1fr] gap-4">
        {/* Liste */}
        <div className="apple-card p-3 h-max">
          <div className="flex gap-2 mb-3">
            <button onClick={nouveau} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-navy-800 text-white text-[13px] hover:bg-navy-900"><Plus size={14} /> Nouveau</button>
            <button onClick={() => fileRef.current && fileRef.current.click()} title="Importer un .docx" className="px-3 py-2 rounded-lg border border-cream-300 text-navy-600 hover:border-navy-300"><Upload size={15} /></button>
            <input ref={fileRef} type="file" accept=".docx,.txt" onChange={onImport} className="hidden" />
          </div>
          <div className="flex flex-col gap-1">
            {modeles.length === 0 && <div className="text-[12.5px] text-navy-300 px-2 py-4 text-center">Aucun modèle.<br />Créez-en un ou importez un .docx.</div>}
            {modeles.map(m => (
              <button key={m.id} onClick={() => openModele(m.id)}
                className={`text-left px-3 py-2 rounded-lg text-[13px] transition-colors ${selId === m.id ? 'bg-gold-100 text-navy-900 font-medium' : 'text-navy-600 hover:bg-cream-100'}`}>
                <div className="truncate">{m.nom}</div>
                <div className="text-[11px] text-navy-300 capitalize">{m.categorie}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Éditeur */}
        <div className="apple-card p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            <input value={form.nom} onChange={e => { setForm(f => ({ ...f, nom: e.target.value })); setDirty(true); }}
              placeholder="Nom du modèle (ex. Courrier de bienvenue)" className="form-input rounded-xl flex-1 min-w-[220px]" />
            <select value={form.categorie} onChange={e => { setForm(f => ({ ...f, categorie: e.target.value })); setDirty(true); }} className="form-input rounded-xl">
              {CATS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <button onClick={save} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-medium ${dirty ? 'bg-gold-500 text-navy-900' : 'bg-cream-100 text-navy-400'}`}><Save size={15} /> Enregistrer</button>
            {selId && <button onClick={supprimer} className="px-3 py-2 rounded-xl border border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>}
          </div>

          {/* Champs de fusion */}
          <div className="mb-2">
            <div className="text-[11px] uppercase tracking-wide text-navy-400 mb-1.5">Insérer un champ (rempli automatiquement)</div>
            <div className="flex flex-wrap gap-1.5">
              {champs.map(c => (
                <button key={c.cle} onClick={() => insertChamp(c.cle)} title={c.cle}
                  className="text-[11.5px] px-2 py-1 rounded-md bg-navy-50 text-navy-700 border border-navy-100 hover:bg-navy-100">{c.label}</button>
              ))}
            </div>
          </div>

          <textarea ref={textRef} value={form.contenu} onChange={e => { setForm(f => ({ ...f, contenu: e.target.value })); setDirty(true); }}
            rows={16} placeholder="Rédigez votre courrier ou contrat… Utilisez les champs ci-dessus, ex : « Madame, Monsieur {{parents}}, domicilié(e) {{adresse}}… »"
            className="form-input rounded-xl w-full font-mono text-[13px] leading-relaxed" />

          {/* Génération pour un client */}
          <div className="mt-4 pt-4 border-t border-cream-200">
            <div className="text-[11px] uppercase tracking-wide text-navy-400 mb-2 flex items-center gap-1.5"><Users size={13} /> Générer pour un enfant / une famille</div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={enfantId} onChange={e => setEnfantId(e.target.value)} className="form-input rounded-xl min-w-[220px]">
                <option value="">— Choisir un enfant —</option>
                {enfants.map(en => <option key={en.id} value={en.id}>{en.prenom} {en.nom}{en.structure_nom ? ` · ${en.structure_nom}` : ''}</option>)}
              </select>
              <button onClick={voirApercu} disabled={!selId} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-cream-300 text-navy-600 hover:border-navy-300 disabled:opacity-40"><Eye size={15} /> Aperçu</button>
              {selId && (
                <a href={courriersApi.pdfUrl(selId, enfantId)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-navy-800 text-white text-[13px] hover:bg-navy-900"><Download size={15} /> Télécharger le PDF</a>
              )}
            </div>
            {apercu && (
              <div className="mt-3 apple-card p-4 bg-cream-50 whitespace-pre-wrap text-[13px] text-navy-800 leading-relaxed">{apercu}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
